/**
 * Shared logic for the Overview-style hero card (greeting + 4 summary tiles).
 * Used by /overview and /dashboard so both stay in sync.
 */
import { parseLocalDate, toLocalDateKey, todayKey, localDateDisplay, shortDateDisplay } from './dateUtils'
import { normalizeList } from './normalize'

export function getOverviewUserName(): string {
  try {
    const raw = localStorage.getItem('dizeeUser')
    if (!raw) return 'User'
    const user = JSON.parse(raw)
    return user?.name || user?.username || user?.email?.split('@')[0] || 'User'
  } catch {
    return 'User'
  }
}

/** Prefer artist / stage name (e.g. SIDEPIECE), then profile name — for dashboard “Hey, …” line. */
export function getDashboardGreetingName(): string {
  try {
    const raw = localStorage.getItem('dizeeUser')
    if (!raw) return 'User'
    const user = JSON.parse(raw)
    const artist = String(user?.artistName || user?.artist_name || user?.artist || '').trim()
    if (artist) return artist
    return user?.name || user?.username || user?.email?.split('@')[0] || 'User'
  } catch {
    return 'User'
  }
}

function extractArray(payload: unknown): any[] {
  const p = payload as any
  const list = p?.data?.data || p?.data || p || []
  if (Array.isArray(list)) return list
  if (Array.isArray(list?.data)) return list.data
  return []
}

function getTomorrowKey(): string {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return toLocalDateKey(tomorrow)
}

interface HeroShow {
  id: string
  date?: string
  perf_date?: string
  title?: string
  event_title?: string
  venue?: string
  city?: string
  state?: string
  show_time?: string
  tix_sold?: number
  sellable_cap?: string | number
  capacity?: string | number
  status?: string
}

interface HeroTask {
  id: string
  title: string
  due_date?: string
  status: string
  priority?: string
  category?: string
}

interface HeroSong {
  id: string
  song_title?: string
  title?: string
  release_date?: string
  streams_total?: number
  streams_today?: number
  artist_name?: string
  /** Featured artists, comma-separated or single string from API */
  featuring?: string
  pre_saves?: number
  ig_creates?: number
  tt_creates?: number
}

interface CalendarEvent {
  id: string
  title: string
  start: string
  location?: string
}

export interface ScheduleItem {
  id: string
  type: 'show' | 'task' | 'release' | 'flight' | 'hotel' | 'calendar' | 'transport'
  title: string
  time?: string
  location?: string
  date: string
  details?: string
}

interface TaskActionStub {
  category: 'task'
  title: string
  dueDate?: string
}

const CANCELED_SHOW_STATUSES = new Set(['canceled', 'cancelled'])

function normalizeBookingsPayload(payload: unknown): HeroShow[] {
  return extractArray(payload).filter((item: any) => !CANCELED_SHOW_STATUSES.has((item.status || '').toLowerCase().trim())).map((item: any) => ({
    id: item._id || item.id,
    date: item.date || item.perf_date,
    perf_date: item.perf_date || item.date,
    title: item.event_title || item.title || item.venue,
    event_title: item.event_title || item.title,
    venue: item.venue,
    city: item.city,
    state: item.state,
    tix_sold: item.tix_sold ?? item.current_ticket_count ?? item.tickets_sold ?? item.ticket_count ?? 0,
    sellable_cap: item.sellable_cap,
    capacity: item.capacity,
    show_time: item.show_time,
    status: item.status,
  }))
}

function toOptionalNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined
  const n = Number(value)
  return Number.isNaN(n) ? undefined : n
}

function normalizeCatalogPayload(payload: unknown): HeroSong[] {
  return extractArray(payload).map((item: any) => {
    const rawStreamsTotal =
      item.streams_total ??
      item.streams ??
      item.streamsTotal ??
      item?.soundcharts_data?.streams ??
      item?.soundcharts_data?.monthly_listeners ??
      0
    const rawStreamsToday =
      item.streams_today ??
      item.streamsToday ??
      item.streams_daily ??
      item.streamsDaily ??
      item.daily_streams ??
      item.dailyStreams
    const feat =
      item.featuring ||
      item.featured_artists ||
      item.featuredArtists ||
      (Array.isArray(item.featuring_artists) ? item.featuring_artists.join(', ') : undefined)
    const preRaw =
      item.pre_saves ??
      item.presave_count ??
      item.presaves ??
      item.presaveCount ??
      item?.release_campaign?.pre_saves
    const igRaw = item.instagram_creates ?? item.ig_creates ?? item.igCreates ?? item?.release_campaign?.instagram_creates
    const ttRaw = item.tiktok_creates ?? item.tt_creates ?? item.ttCreates ?? item?.release_campaign?.tiktok_creates

    return {
      id: item._id || item.id,
      song_title: item.song_title || item.title || item.songName,
      title: item.title || item.song_title,
      streams_total: Number(rawStreamsTotal) || 0,
      streams_today: toOptionalNumber(rawStreamsToday),
      release_date: item.release_date || item.releaseDate,
      artist_name: item.artist_name || item.artistName || item.artist,
      featuring: typeof feat === 'string' ? feat : undefined,
      pre_saves: toOptionalNumber(preRaw),
      ig_creates: toOptionalNumber(igRaw),
      tt_creates: toOptionalNumber(ttRaw),
    }
  })
}

function normalizeCalendarEvents(calendarData: unknown): CalendarEvent[] {
  const p = calendarData as any
  const events = p?.data?.data?.events || []
  if (!Array.isArray(events)) return []
  return events.map((ev: any) => ({
    id: ev.id || `cal-${Math.random()}`,
    title: ev.summary || '(No title)',
    start: ev.start?.dateTime || ev.start?.date || '',
    location: ev.location || '',
  }))
}

function compileSchedule(
  shows: HeroShow[],
  tasks: HeroTask[],
  songs: HeroSong[],
  calendarEvents: CalendarEvent[],
): { scheduleToday: ScheduleItem[]; scheduleTomorrow: ScheduleItem[] } {
  const today = todayKey()
  const tomorrow = getTomorrowKey()
  const todayItems: ScheduleItem[] = []
  const tomorrowItems: ScheduleItem[] = []

  shows.forEach((show) => {
    const showDate = show.date || show.perf_date || ''
    const base = {
      id: `show-${show.id}`,
      type: 'show' as const,
      title: show.event_title || show.title || show.venue || 'Show',
      time: show.show_time,
      location: `${show.venue || ''}${show.venue && show.city ? ' • ' : ''}${show.city || ''}`,
      date: showDate,
      details: show.tix_sold ? `${show.tix_sold} tickets` : undefined,
    }
    if (showDate === today) todayItems.push(base)
    else if (showDate === tomorrow) tomorrowItems.push(base)
  })

  tasks.forEach((task) => {
    if (!task.due_date || task.status !== 'pending') return
    const taskDate = toLocalDateKey(parseLocalDate(task.due_date) || new Date(0))
    const item: ScheduleItem = {
      id: `task-${task.id}`,
      type: 'task',
      title: task.title,
      date: taskDate,
      details: task.priority ? `${task.priority} priority` : undefined,
    }
    if (taskDate === today) todayItems.push(item)
    else if (taskDate === tomorrow) tomorrowItems.push(item)
  })

  songs.forEach((song) => {
    if (!song.release_date) return
    const releaseDate = song.release_date
    const title = `Release: ${song.song_title || song.title || 'Untitled'}`
    if (releaseDate === today) {
      todayItems.push({ id: `release-${song.id}`, type: 'release', title, date: releaseDate })
    } else if (releaseDate === tomorrow) {
      tomorrowItems.push({ id: `release-${song.id}`, type: 'release', title, date: releaseDate })
    }
  })

  calendarEvents.forEach((event) => {
    const eventDate = event.start.split('T')[0]
    const item: ScheduleItem = {
      id: `calendar-${event.id}`,
      type: 'calendar',
      title: event.title,
      location: event.location,
      date: eventDate,
      time: event.start ? new Date(event.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : undefined,
    }
    if (eventDate === today) todayItems.push(item)
    else if (eventDate === tomorrow) tomorrowItems.push(item)
  })

  const sortByTime = (a: ScheduleItem, b: ScheduleItem) => {
    if (a.time && b.time) return a.time.localeCompare(b.time)
    if (a.time) return -1
    if (b.time) return 1
    return 0
  }

  return {
    scheduleToday: todayItems.sort(sortByTime),
    scheduleTomorrow: tomorrowItems.sort(sortByTime),
  }
}

/** Mirrors Overview “due today / overdue” task stubs for the Tasks tile fallback. */
function taskStubsFromTasks(tasks: HeroTask[]): TaskActionStub[] {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const stubs: TaskActionStub[] = []
  for (const task of tasks) {
    if (task.status !== 'pending' || !task.due_date) continue
    const d = parseLocalDate(task.due_date)
    if (!d) continue
    d.setHours(0, 0, 0, 0)
    const daysUntil = Math.ceil((d.getTime() - today.getTime()) / (86_400_000))
    if (daysUntil <= 0) {
      stubs.push({ category: 'task', title: task.title, dueDate: task.due_date })
    }
  }
  return stubs
}

function upcomingShowsFromBookings(shows: HeroShow[]): HeroShow[] {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  return shows
    .filter((s) => {
      const showDate = parseLocalDate(s.date || s.perf_date || '')
      return (
        showDate &&
        showDate >= now &&
        (s.status === 'upcoming' || s.status === 'confirmed' || s.status === 'announced')
      )
    })
    .sort(
      (a, b) =>
        (parseLocalDate(a.date || a.perf_date || '')?.getTime() ?? 0) -
        (parseLocalDate(b.date || b.perf_date || '')?.getTime() ?? 0),
    )
    .slice(0, 3)
}

/** First segment of schedule show `location` (“Venue • City”) = venue name. */
export function venueFromScheduleLocation(item: ScheduleItem): string {
  if (item.type !== 'show' || !item.location) return ''
  return item.location.split(' • ')[0]?.trim() || ''
}

const releaseTitleStrip = (title: string) => title.replace(/^Release: /, '')

/** Bold headline under “Yo, …” — shorter when a show is featured so the gray tagline carries venue context. */
export function heroMainLineFromFeature(
  featureItem: ScheduleItem | undefined,
  isFeatureToday: boolean,
): string {
  if (!featureItem) return 'All clear for today'

  if (featureItem.type === 'show') {
    const venue = venueFromScheduleLocation(featureItem)
    const evt = featureItem.title
    const timePart = featureItem.time ? ` · ${featureItem.time}` : ''
    if (isFeatureToday) {
      if (venue && evt && evt.toLowerCase() !== venue.toLowerCase()) return `${evt}${timePart}`
      return venue ? `Tonight · ${venue}${timePart}` : `Tonight${timePart}`
    }
    if (venue && evt && evt.toLowerCase() !== venue.toLowerCase()) return `${evt}${timePart}`
    return venue ? `Tomorrow · ${venue}${timePart}` : `Tomorrow${timePart}`
  }

  if (isFeatureToday && featureItem.type === 'release') {
    return `New release: ${releaseTitleStrip(featureItem.title)}`
  }
  if (isFeatureToday && featureItem.type === 'task') {
    return `${featureItem.title}${featureItem.time ? ` at ${featureItem.time}` : ''}`
  }
  if (isFeatureToday) {
    return `${featureItem.title}${featureItem.time ? ` at ${featureItem.time}` : ''}`
  }
  if (featureItem.type === 'release') {
    return `${releaseTitleStrip(featureItem.title)}${featureItem.time ? ` at ${featureItem.time}` : ''}`
  }
  if (featureItem.time) {
    return `${featureItem.title} at ${featureItem.time}`
  }
  return featureItem.title
}

type HeroTaglineShow = {
  date?: string
  perf_date?: string
  title?: string
  event_title?: string
  venue?: string
  show_time?: string
}

/**
 * Gray contextual line under the hero headline (replaces static “Your day at a glance.”).
 */
export function computeHeroTagline(
  featureItem: ScheduleItem | undefined,
  isFeatureToday: boolean,
  nextShow: HeroTaglineShow | null,
  nextShowIsToday: boolean,
): string {
  // Match the bold headline: describe the featured today/tomorrow item first.
  if (featureItem?.type === 'show') {
    const v = venueFromScheduleLocation(featureItem)
    const evt = featureItem.title
    if (v) {
      const tail = evt && evt.toLowerCase() !== v.toLowerCase() ? ` — ${evt}` : ''
      return isFeatureToday
        ? `You have a show today at ${v}${tail}.`
        : `You have a show tomorrow at ${v}${tail}.`
    }
    return isFeatureToday
      ? `You have a show today: ${evt}.`
      : `You have a show tomorrow: ${evt}.`
  }

  if (featureItem?.type === 'task') {
    const when = isFeatureToday ? 'today' : 'tomorrow'
    return `Next up${featureItem.time ? ` at ${featureItem.time}` : ''}: ${featureItem.title} (${when}).`
  }

  if (featureItem?.type === 'release') {
    const when = isFeatureToday ? 'today' : 'tomorrow'
    return `${releaseTitleStrip(featureItem.title)} comes out ${when}.`
  }

  if (featureItem?.type === 'calendar') {
    return `${featureItem.title}${featureItem.location ? ` at ${featureItem.location}` : ''} ${isFeatureToday ? 'today' : 'tomorrow'}.`
  }

  if (
    featureItem &&
    (featureItem.type === 'flight' ||
      featureItem.type === 'hotel' ||
      featureItem.type === 'transport')
  ) {
    return `${featureItem.title}${featureItem.location ? ` — ${featureItem.location}` : ''} ${isFeatureToday ? 'today' : 'tomorrow'}.`
  }

  if (featureItem) {
    return `${featureItem.title}${featureItem.time ? ` at ${featureItem.time}` : ''} ${isFeatureToday ? 'today' : 'tomorrow'}.`
  }

  // Nothing today/tomorrow on the schedule — use next confirmed/upcoming booking if any.
  if (nextShow) {
    const dk = nextShow.date || nextShow.perf_date || ''
    const venue = nextShow.venue?.trim() || ''
    const eventName = (nextShow.event_title || nextShow.title || '').trim()
    const nameDiff = Boolean(venue && eventName && eventName.toLowerCase() !== venue.toLowerCase())
    const tail = nameDiff ? ` — ${eventName}` : ''
    if (venue) {
      if (nextShowIsToday) {
        return `You have a show today at ${venue}${tail}.`
      }
      const datePart = dk ? ` on ${shortDateDisplay(dk)}` : ''
      return `You have a show at ${venue}${datePart}${tail}.`
    }
    if (eventName) {
      const datePart = dk && !nextShowIsToday ? ` on ${shortDateDisplay(dk)}` : ''
      return nextShowIsToday
        ? `You have a show today: ${eventName}.`
        : `You have a show${datePart}: ${eventName}.`
    }
    return 'You have an upcoming show on the calendar.'
  }

  return "Nothing on your calendar today or tomorrow — you're wide open."
}

export interface OverviewHeroCardModel {
  scheduleToday: ScheduleItem[]
  scheduleTomorrow: ScheduleItem[]
  heroMainLine: string
  heroTagline: string
  nextTaskDisplay: string
  nextTaskSubtext: string
  nextShowIsToday: boolean
  nextShowDisplay: string
  nextShowSubtext: string
  latestReleaseDisplay: string
  latestReleaseSubtext: string
}

/**
 * Build hero card fields from the same raw API payloads Overview uses.
 */
export function computeOverviewHeroCardModel(
  showsData: unknown,
  songsData: unknown,
  tasksData: unknown,
  calendarData: unknown,
): OverviewHeroCardModel {
  const shows = normalizeBookingsPayload(showsData)
  const songs = normalizeCatalogPayload(songsData)
  const tasks = normalizeList<HeroTask>(extractArray(tasksData) as HeroTask[])
  const calendarEvents = normalizeCalendarEvents(calendarData)

  const { scheduleToday, scheduleTomorrow } = compileSchedule(shows, tasks, songs, calendarEvents)
  const taskStubs = taskStubsFromTasks(tasks)
  const today = todayKey()
  const tomorrowKey = getTomorrowKey()

  const typePriority = (t: ScheduleItem['type']) =>
    t === 'calendar' ? 0 : t === 'task' ? 1 : t === 'show' ? 2 : t === 'release' ? 3 : 2
  const byPriorityThenTime = (a: ScheduleItem, b: ScheduleItem) => {
    const pa = typePriority(a.type)
    const pb = typePriority(b.type)
    if (pa !== pb) return pa - pb
    if (a.time && b.time) return a.time.localeCompare(b.time)
    if (a.time) return -1
    if (b.time) return 1
    return 0
  }
  const sortedToday = [...scheduleToday].sort(byPriorityThenTime)
  const sortedTomorrow = [...scheduleTomorrow].sort(byPriorityThenTime)
  const featureItem = sortedToday[0] || sortedTomorrow[0]
  const isFeatureToday = sortedToday.length > 0

  const heroMainLine = heroMainLineFromFeature(featureItem, isFeatureToday)

  const tasksTodayOnly = scheduleToday.filter((i) => i.type === 'task')
  const tasksTomorrowOnly = scheduleTomorrow.filter((i) => i.type === 'task')
  const nextTaskFromSchedule = tasksTodayOnly[0] || tasksTomorrowOnly[0]
  const nextTaskFromActions = taskStubs[0]
  const nextTaskDisplay = nextTaskFromSchedule
    ? `Task: ${nextTaskFromSchedule.title}${nextTaskFromSchedule.time ? ` · ${nextTaskFromSchedule.time}` : ''}`
    : nextTaskFromActions
      ? `Task: ${nextTaskFromActions.title}`
      : 'Nothing due'
  const nextTaskSubtext = nextTaskFromSchedule
    ? nextTaskFromSchedule.date === today
      ? 'Today'
      : localDateDisplay(nextTaskFromSchedule.date, { weekday: 'short' })
    : nextTaskFromActions?.dueDate
      ? toLocalDateKey(parseLocalDate(nextTaskFromActions.dueDate) || new Date()) === today
        ? 'Due today'
        : `Due ${shortDateDisplay(nextTaskFromActions.dueDate)}`
      : ''

  const nowForSongs = new Date()
  nowForSongs.setHours(0, 0, 0, 0)
  const releasedSongs = songs
    .filter((s) => s.release_date && (parseLocalDate(s.release_date)?.getTime() ?? 0) <= nowForSongs.getTime())
    .sort(
      (a, b) =>
        (parseLocalDate(b.release_date!)?.getTime() ?? 0) - (parseLocalDate(a.release_date!)?.getTime() ?? 0),
    )

  const latestReleaseIsToday =
    releasedSongs.length > 0 &&
    releasedSongs[0].release_date &&
    toLocalDateKey(parseLocalDate(releasedSongs[0].release_date!) || new Date(0)) === today
  const latestReleaseDisplay =
    releasedSongs.length > 0
      ? latestReleaseIsToday
        ? `New release: ${releasedSongs[0].song_title || releasedSongs[0].title || 'Release'}`
        : releasedSongs[0].song_title || releasedSongs[0].title || 'Release'
      : 'No recent'
  const latestReleaseSubtext =
    releasedSongs.length > 0
      ? latestReleaseIsToday
        ? 'Out today'
        : releasedSongs[0].streams_total != null
          ? `${Number(releasedSongs[0].streams_total).toLocaleString()} streams`
          : ''
      : ''

  const upcoming = upcomingShowsFromBookings(shows)
  const nextShow = upcoming[0] || null
  const nextShowIsToday =
    !!nextShow &&
    !!(nextShow.date || nextShow.perf_date) &&
    toLocalDateKey(parseLocalDate(nextShow.date || nextShow.perf_date!) || new Date(0)) === today
  const nextShowDateKey = nextShow ? (nextShow.date || nextShow.perf_date || '') : ''
  const nextShowEventName = nextShow ? (nextShow.event_title || nextShow.title || '').trim() : ''
  const nextShowVenue = nextShow?.venue?.trim() || ''
  const nextShowNameDiffersFromVenue = Boolean(
    nextShowEventName &&
      nextShowVenue &&
      nextShowEventName.toLowerCase() !== nextShowVenue.toLowerCase(),
  )

  const nextShowIsTomorrow = Boolean(
    nextShowDateKey && nextShowDateKey === tomorrowKey && !nextShowIsToday,
  )

  const nextShowDisplay = !nextShow
    ? 'None'
    : nextShowVenue
      ? nextShowIsToday
        ? `You have a show today at ${nextShowVenue}`
        : nextShowIsTomorrow
          ? `You have a show tomorrow at ${nextShowVenue}`
          : `You have a show at ${nextShowVenue}`
      : nextShowEventName
        ? nextShowIsToday
          ? `You have a show today: ${nextShowEventName}`
          : nextShowIsTomorrow
            ? `You have a show tomorrow: ${nextShowEventName}`
            : `You have a show: ${nextShowEventName}`
        : 'Upcoming show'

  const nextShowSubtext = (() => {
    if (!nextShow) return ''
    const parts: string[] = []
    if (nextShowNameDiffersFromVenue) parts.push(nextShowEventName)
    if (!nextShowIsToday && nextShowDateKey) parts.push(shortDateDisplay(nextShowDateKey))
    if (nextShow.show_time) parts.push(nextShow.show_time)
    return parts.join(' · ')
  })()

  const heroTagline = computeHeroTagline(featureItem, isFeatureToday, nextShow, nextShowIsToday)

  return {
    scheduleToday,
    scheduleTomorrow,
    heroMainLine,
    heroTagline,
    nextTaskDisplay,
    nextTaskSubtext,
    nextShowIsToday,
    nextShowDisplay,
    nextShowSubtext,
    latestReleaseDisplay,
    latestReleaseSubtext,
  }
}

// ── Dashboard-only hero (three tiles: today’s tasks, next show, next release) ──

function parseSellableCapacity(show: HeroShow): number {
  const raw = show.sellable_cap ?? show.capacity
  if (raw == null || raw === '') return 0
  const n = Number(String(raw).replace(/[^0-9]/g, ''))
  return Number.isFinite(n) ? n : 0
}

function taskIsCompletedForDashboard(status: string | undefined): boolean {
  const s = (status || '').toLowerCase()
  return s === 'completed' || s === 'done' || s === 'cancelled' || s === 'canceled'
}

/** Max tasks in the dashboard hero “Next upcoming tasks” list (pagination uses the same page size). */
export const DASHBOARD_HERO_TASKS_PER_PAGE = 4

/**
 * Due today + overdue first, then upcoming tasks by due date, up to {@link DASHBOARD_HERO_TASKS_PER_PAGE}.
 * (If you only had three due/overdue, the fourth slot is the next pending task so the column can show four lines.)
 */
function collectTodayDashboardTasks(tasks: HeroTask[]): { id: string; title: string; overdue: boolean; href: string }[] {
  const todayK = todayKey()
  const dueOrOverdue: { id: string; title: string; overdue: boolean; href: string }[] = []
  const upcoming: { id: string; title: string; overdue: boolean; dueKey: string; href: string }[] = []

  for (const task of tasks) {
    if (!task.due_date || taskIsCompletedForDashboard(task.status)) continue
    const d = parseLocalDate(task.due_date)
    if (!d) continue
    const dk = toLocalDateKey(d)
    const id = String((task as { _id?: string })._id ?? task.id ?? '')
    if (!id) continue
    const href = `/tasks?focus=${encodeURIComponent(id)}`
    const row = {
      id,
      title: task.title?.trim() || 'Untitled task',
      overdue: dk < todayK,
      href,
    }
    if (dk <= todayK) dueOrOverdue.push(row)
    else upcoming.push({ ...row, dueKey: dk })
  }

  dueOrOverdue.sort((a, b) => {
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1
    return a.title.localeCompare(b.title)
  })
  upcoming.sort((a, b) => a.dueKey.localeCompare(b.dueKey) || a.title.localeCompare(b.title))

  const upcomingRows = upcoming.map(({ id, title, overdue, href }) => ({ id, title, overdue, href }))
  const merged = [...dueOrOverdue, ...upcomingRows]
  return merged.slice(0, DASHBOARD_HERO_TASKS_PER_PAGE)
}

function pickNextUpcomingShowForDashboard(shows: HeroShow[]): HeroShow | null {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  const candidates = shows.filter((s) => {
    const showDate = parseLocalDate(s.date || s.perf_date || '')
    if (!showDate || showDate < now) return false
    const st = (s.status || '').toLowerCase()
    if (st === 'canceled' || st === 'cancelled') return false
    return true
  })
  candidates.sort(
    (a, b) =>
      (parseLocalDate(a.date || a.perf_date || '')?.getTime() ?? 0) -
      (parseLocalDate(b.date || b.perf_date || '')?.getTime() ?? 0),
  )
  return candidates[0] || null
}

function formatDashboardVenueLine(show: HeroShow): string {
  const venue = show.venue?.trim() || ''
  const city = show.city?.trim() || ''
  const state = show.state?.trim() || ''
  const loc = [city, state].filter(Boolean).join(', ')
  const parts = [venue, loc].filter(Boolean)
  if (parts.length > 0) return parts.join(', ')
  return show.event_title?.trim() || show.title?.trim() || 'Upcoming show'
}

function formatDashboardShowDetailLine(show: HeroShow): string {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const sd = parseLocalDate(show.date || show.perf_date || '')
  if (!sd) return ''
  const days = Math.ceil((sd.getTime() - today.getTime()) / 86_400_000)
  const sold = Number(show.tix_sold) || 0
  const cap = parseSellableCapacity(show)
  const parts: string[] = []
  if (cap > 0) {
    parts.push(`${sold.toLocaleString()}/${cap.toLocaleString()} tickets`)
    const pct = Math.round((sold / cap) * 100)
    parts.push(`${pct}% sold`)
  }
  // No sellable cap: omit raw ticket counts here — hero tile shows a single pacing hint instead of repeating sold
  if (days >= 0) {
    parts.push(`${days} day${days === 1 ? '' : 's'} till show`)
  }
  return parts.join(' · ')
}

function pickNextUpcomingReleaseSong(songs: HeroSong[]): HeroSong | null {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  const future = songs.filter((s) => {
    const rd = parseLocalDate(s.release_date)
    return rd && rd >= now
  })
  future.sort(
    (a, b) =>
      (parseLocalDate(a.release_date!)?.getTime() ?? 0) - (parseLocalDate(b.release_date!)?.getTime() ?? 0),
  )
  return future[0] || null
}

function formatDashboardArtistLine(song: HeroSong): string {
  const main = song.artist_name?.trim()
  const feat = song.featuring?.trim()
  if (main && feat) return `${main}, ${feat}`
  return main || feat || ''
}

/** Shared with Music insight card so release campaign numbers match the dashboard hero. */
export function formatDashboardMetric(n: number | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  return n.toLocaleString()
}

function songsReleasingToday(songs: HeroSong[], tk: string): HeroSong[] {
  const list = songs.filter((s) => {
    if (!s.release_date) return false
    const rd = parseLocalDate(s.release_date)
    if (!rd) return false
    return toLocalDateKey(rd) === tk
  })
  list.sort((a, b) =>
    (a.song_title || a.title || '').localeCompare(b.song_title || b.title || ''),
  )
  return list
}

function showsHappeningToday(shows: HeroShow[], tk: string): HeroShow[] {
  const list = shows.filter((s) => {
    const dk = toLocalDateKey(parseLocalDate(s.date || s.perf_date || '') || new Date(0))
    if (dk !== tk) return false
    const st = (s.status || '').toLowerCase()
    if (st === 'canceled' || st === 'cancelled') return false
    return true
  })
  list.sort((a, b) => (a.venue || '').localeCompare(b.venue || ''))
  return list
}

function showTonightLabel(show: HeroShow): string {
  const v = show.venue?.trim()
  if (v) return v
  return show.event_title?.trim() || show.title?.trim() || 'Show'
}

function pickHighlightTaskForGreeting(tasks: HeroTask[]): { title: string } | null {
  const pending = tasks.filter((t) => !taskIsCompletedForDashboard(t.status))
  if (pending.length === 0) return null

  const tk = todayKey()
  const withDue = pending.filter((t) => t.due_date)
  const dueToday = withDue.filter((t) => toLocalDateKey(parseLocalDate(t.due_date) || new Date(0)) === tk)
  dueToday.sort((a, b) => (a.title || '').localeCompare(b.title || ''))
  if (dueToday.length > 0) {
    return { title: dueToday[0].title?.trim() || 'Task' }
  }

  const future = withDue.filter((t) => toLocalDateKey(parseLocalDate(t.due_date) || new Date(0)) > tk)
  future.sort(
    (a, b) =>
      (parseLocalDate(a.due_date)?.getTime() ?? 0) - (parseLocalDate(b.due_date)?.getTime() ?? 0),
  )
  if (future.length > 0) {
    return { title: future[0].title?.trim() || 'Task' }
  }

  const overdue = withDue.filter((t) => toLocalDateKey(parseLocalDate(t.due_date) || new Date(0)) < tk)
  overdue.sort(
    (a, b) =>
      (parseLocalDate(a.due_date)?.getTime() ?? 0) - (parseLocalDate(b.due_date)?.getTime() ?? 0),
  )
  if (overdue.length > 0) {
    return { title: overdue[0].title?.trim() || 'Task' }
  }

  return { title: pending[0].title?.trim() || 'Task' }
}

function computeDashboardHeroGreeting(
  shows: HeroShow[],
  songs: HeroSong[],
  tasks: HeroTask[],
): DashboardHeroGreeting {
  const heyName = getDashboardGreetingName()
  const tk = todayKey()

  const releasesToday = songsReleasingToday(songs, tk)
  if (releasesToday.length > 0) {
    const song = releasesToday[0]
    const label = song.song_title || song.title || 'Release'
    return {
      heyName,
      headline: `Tonight - ${label}`,
      highlightTaskTitle: null,
    }
  }

  const showsToday = showsHappeningToday(shows, tk)
  if (showsToday.length > 0) {
    const show = showsToday[0]
    return {
      heyName,
      headline: `Tonight - ${showTonightLabel(show)}`,
      highlightTaskTitle: null,
    }
  }

  const intro = "Here's what you need to accomplish today…"
  const hit = pickHighlightTaskForGreeting(tasks)
  return {
    heyName,
    headline: intro,
    highlightTaskTitle: hit?.title ?? null,
  }
}

export interface DashboardHeroGreeting {
  /** First line: “Hey, {heyName},” */
  heyName: string
  /** Second line: “Tonight - …” or the tasks intro */
  headline: string
  /** When no show/release today: optional highlighted task title under the intro */
  highlightTaskTitle?: string | null
}

export interface DashboardHeroCardModel {
  greeting: DashboardHeroGreeting
  todayTasks: { id: string; title: string; overdue: boolean; href: string }[]
  tasksHref: string
  nextShow: {
    id?: string
    venueLine: string
    detailLine: string
    ticketsSold: number
    sellableCap: number
    href: string
  } | null
  nextRelease: {
    id?: string
    title: string
    artistLine: string
    daysUntil: number | null
    preSaves: string
    igCreates: string
    ttCreates: string
    href: string
  } | null
}

/**
 * Intelligence / Dashboard header: today’s tasks (due today + overdue), next show, next release.
 */
export function computeDashboardHeroCardModel(
  showsData: unknown,
  songsData: unknown,
  tasksData: unknown,
): DashboardHeroCardModel {
  const shows = normalizeBookingsPayload(showsData)
  const songs = normalizeCatalogPayload(songsData)
  const tasks = normalizeList<HeroTask>(extractArray(tasksData) as HeroTask[])

  const todayTasks = collectTodayDashboardTasks(tasks)
  const greeting = computeDashboardHeroGreeting(shows, songs, tasks)

  const next = pickNextUpcomingShowForDashboard(shows)
  const nextShow = next
    ? {
        id: String(next.id),
        venueLine: formatDashboardVenueLine(next),
        detailLine: formatDashboardShowDetailLine(next),
        ticketsSold: Number(next.tix_sold) || 0,
        sellableCap: parseSellableCapacity(next),
        href: `/shows?focus=${encodeURIComponent(String(next.id))}`,
      }
    : null

  const song = pickNextUpcomingReleaseSong(songs)
  if (!song) {
    return { greeting, todayTasks, tasksHref: '/tasks', nextShow, nextRelease: null }
  }

  const rd = parseLocalDate(song.release_date)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const daysUntil = rd ? Math.ceil((rd.getTime() - today.getTime()) / 86_400_000) : null

  return {
    greeting,
    todayTasks,
    tasksHref: '/tasks',
    nextShow,
    nextRelease: {
      id: String(song.id),
      title: song.song_title || song.title || 'Untitled',
      artistLine: formatDashboardArtistLine(song),
      daysUntil,
      preSaves: formatDashboardMetric(song.pre_saves),
      igCreates: formatDashboardMetric(song.ig_creates),
      ttCreates: formatDashboardMetric(song.tt_creates),
      href: `/music?focus=${encodeURIComponent(String(song.id))}`,
    },
  }
}
